/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Dimension } from '#utils/UNSAFE_visTypes/common';
import { Font } from '#hooks/PRIVATE_useTextDimensions';
import { BarItemData } from './barChart.types';
import { styles } from '#PRIVATE_Chart/themes/ChartStyles.css';
import { canFitLabel, getLabelProps } from './utils/barItemLabelUtils';
import { VNode } from 'preact';
import { getContrastingTextColor } from '#utils/PRIVATE_colorUtils/colorUtils';

type Props<K> = {
  item: BarItemData<K>;
  seriesColor?: string;
  isStacked: boolean;
  defaultFontSize: string;
  isHoriz: boolean;
  barDim: Dimension;
  getTextDimensions?: (text: string, style: Font) => Dimension;
  isRtl: boolean;
};

export function BarLabel<K>({
  item,
  seriesColor,
  isStacked,
  defaultFontSize,
  barDim,
  isRtl,
  isHoriz,
  getTextDimensions
}: Props<K>) {
  let label: VNode | null = null;
  const position =
    !item.labelPosition || (item.labelPosition === 'outsideBarEdge' && isStacked)
      ? 'center'
      : item.labelPosition;
  const labelString = item.label;
  const color = item.color || seriesColor;
  const needsContrastColor = isStacked || !(position === 'outsideBarEdge');

  if (
    position != 'none' &&
    labelString &&
    getTextDimensions &&
    !(isStacked && position === 'outsideBarEdge')
  ) {
    const labelStyle = {
      fontSize: defaultFontSize,
      fill: needsContrastColor ? getContrastingTextColor(color!) : undefined,
      ...item.labelStyle
    };
    const labelDims = getTextDimensions(labelString, labelStyle);
    const canFit = canFitLabel(isHoriz, labelDims, barDim, position || 'center');
    label = canFit ? (
      <text
        class={styles.dataLabelStyle}
        style={labelStyle}
        {...getLabelProps(
          isHoriz,
          isRtl,
          item.value < 0,
          barDim,
          position || 'center',
          getTextDimensions?.(labelString, labelStyle)
        )}>
        {labelString}
      </text>
    ) : null;
  }

  return label;
}
